import { useEffect, useRef, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { FaDownload } from "react-icons/fa";
import { FaRegFilePdf, FaRegImage } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import imageCompression from 'browser-image-compression';

import Loader from "src/Components/Common/Loader";
import WhiteUploadButton from "src/Components/Common/whiteuploadbutton";
import { setPONumber } from "src/Redux/Slices/HeaderSlice";
import PoReceiptScanFlow from "./Components/PoReceiptScanFlow";
import WOService from "src/Services/API/wo.service";
import PONumberAddModal from "./Components/ponumberaddmodal";
import ConfirmDialog from "src/Common/confirmdialog";
import AttachmentImageSlider from "../WO/Components/attachment/AttachmentImageSlider";

import {
  formatDate,
  formatMoney,
  getAvatarUrl,
  getImageUrl,
  getPOReceiptUrl,
  getRealFileName,
  getUploadUrl,
} from "src/Common/common";

// import ApproveRequestButton from "./Components/CreateModal/approverequestbutton";
// import DenyRequestButton from "./Components/CreateModal/denyrequestbutton";
import UserService from "src/Services/API/user.service";
// import AssignTechModal from "./Components/AssignTech/assigntechmodal";
// import ScheduleModal from "./Components/ScheduleModal";
import { paymentItems } from "src/Common/contants";
import {
  can,
  canEditPo,
  downloadImage,
  downloadPdf,
  handleError,
} from "src/Common/function";
import ApproveRequestButton from "src/Components/Common/approverequestbutton";
import DenyRequestButton from "src/Components/Common/denyrequestbutton";
import PencilButton from "src/Components/Common/pencilbutton";
import POService from "src/Services/API/po.service";
import CreatePoModal from "../WO/Components/createpomodal/CreatePoModal";
import POStatusComponent from "./Components/Status/postatuscomponent";
import BlueButton from "src/Components/Common/bluebutton";
import DownloadPdfbutton from "src/Pages/PO/Components/downloadpdfbutton";
import CompleteButton from "./Components/completebutton";
import PreviewDownloadPdfModal from "./Components/pdf/PreviewDownloadPdfModal";

// The work order detail page's field styling, matched here so the two pages
// read as one product. Kept as local constants rather than imported from
// ServiceCall.jsx, which declares its own copies privately.
const LABEL_CLASS =
  "text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase";
const VALUE_CLASS = "text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px]";

function Field({ label, capitalize, children }) {
  return (
    <div className="flex flex-col justify-start items-start gap-1 min-w-0">
      <div className={LABEL_CLASS}>{label}</div>
      <div className={`self-stretch break-words ${VALUE_CLASS} ${capitalize ? "capitalize" : ""}`}>{children}</div>
    </div>
  );
}

/**
 * One attached receipt, as a thumbnail.
 *
 * PDFs get a marked tile rather than a rendered page — thumbnailing one would
 * mean pulling in a renderer for a 76px tile. Images that fail to load get the
 * same treatment: some historical PO attachments have a database row whose file
 * no longer exists, and a broken-image glyph reads as a bug in the page rather
 * than a missing file.
 */
function ReceiptThumbnail({ attachment, onPreview, onDownload, onDelete, canDelete }) {
  const [failed, setFailed] = useState(false);
  const name = getRealFileName(attachment.fileName) || attachment.fileName;
  const isPdf = attachment.fileName?.toLowerCase().endsWith(".pdf");

  return (
    <div className="w-full min-w-0 flex flex-col gap-1">
      <button
        type="button"
        onClick={onPreview}
        title={name}
        className="w-full h-[76px] rounded-lg overflow-hidden bg-[#F2F4F7] border border-[#E5E8F3] flex items-center justify-center cursor-pointer"
      >
        {isPdf || failed ? (
          <div className="flex flex-col items-center gap-1 text-[#7C829A]">
            {isPdf ? <FaRegFilePdf size={20} /> : <FaRegImage size={20} />}
            <span className="font-OpenSansSemiBold text-[10px] leading-[14px] tracking-[0.1px] uppercase">
              {isPdf ? "PDF" : "Unavailable"}
            </span>
          </div>
        ) : (
          <img
            src={getPOReceiptUrl(attachment.fileName)}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setFailed(true)}
          />
        )}
      </button>
      <div className="w-full min-w-0 flex items-center justify-between gap-2">
        <span
          onClick={onPreview}
          title={name}
          className="min-w-0 truncate text-[#1A2332] font-OpenSansRegular text-[12px] leading-[18px] cursor-pointer hover:underline"
        >
          {name}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <div onClick={onDownload} className="cursor-pointer">
            <FaDownload color="#1A2332" size={12} />
          </div>
          {canDelete && (
            <div className="cursor-pointer" onClick={onDelete}>
              <MdDelete color="#1A2332" size={14} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonValue({ avatar, name }) {
  if (!name) return <span>—</span>;
  return (
    <div className="flex items-center gap-2">
      <img src={getAvatarUrl(avatar)} className="w-5 h-5 rounded-full" alt="" />
      <span className="capitalize">{name}</span>
    </div>
  );
}

function PODetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  // Deliberately not seeded from the navigation state. The list hands the
  // whole PO over on navigation, and the browser keeps that in session history
  // across a reload — so the page used to paint a snapshot taken before the
  // last edit and then swap it for the truth a moment later. On a document
  // carrying quantities and prices, that flash reads as data being lost.
  const [order, setOrder] = useState(null);
  const [user] = useState(UserService.getCurrentUser());
  const inputFile = useRef(null);
  const [currentUser] = useState(UserService.getCurrentUser());
  const [poNumberModalShown, setPoNumberModalShown] = useState(false);
  const [cratePOModalShown, setCratePOModalShown] = useState(false);
  const [pdfModalShown, setPdfModalShown] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  //uploading attachment
  const receiptInputFile = useRef(null);
  const receiptDragCounter = useRef(0);

  //attachment preview
  const [isAttachmentSliderShown, setIsAttachmentSliderShown] = useState(false);
  const [sliderAttachments, setSliderAttachments] = useState([]);
  const [sliderActiveIndex, setSliderActiveIndex] = useState(0);

  //attachment delete confirmation
  const [attachmentDeleteModalShown, setAttachmentDeleteModalShown] = useState(false);
  const [attachmentToDeleteId, setAttachmentToDeleteId] = useState(null);

  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [isReceiptDragActive, setIsReceiptDragActive] = useState(false);
  const [isReceiptDragInvalid, setIsReceiptDragInvalid] = useState(false);
  // Receipts uploaded just now, queued for the total-extraction flow.
  const [receiptScanQueue, setReceiptScanQueue] = useState(null);

  useEffect(() => {
    getPurchaseOrder();
  }, []);

  const getPurchaseOrder = () => {
    const poId = id ?? order?.id;
    if (!poId) return;
    POService.getPurchaseOrdersByPoId(poId)
      .then((response) => {
        if (response.status == 200) {
          setOrder(response.data);
          dispatch(setPONumber(String(response.data.number)));
        }
      })
      .catch((error) => handleError(error));
  };

  const denyRequest = () => { };

  const showPoInputModal = () => {
    // setPoNumberModalShown(true);
    approveRequest();
  };

  const approveRequest = () => {
    POService.update({
      id: order.id,
      data: {
        issuedUser: currentUser.id,
        status: 2,
        issuedDate: new Date(),
      },
    })
      .then((response) => {
        if (response.status == 201) {
          //if this is first po, set the status of wo to "parts"
          if (response.data.wo?.pos?.length === 1) {
            WOService.update({ id: response.data.wo.id, data: { status: 4 } })
              .then((response) => {
                if (response.status == 201) {
                  console.log("status updated to 'Parts' successfully");
                }
              })
              .catch((error) => {
                handleError(error);
              });
          }
          console.log("approve po reqeuest response.data: ", response.data);
          setOrder(response.data);
          dispatch(setPONumber(response.data.number));
          toast("Purchase order approved");
        }
      })
      .catch((error) => {
        handleError(error);
      });
    // setPoNumberModalShown(false);
  };

  const editPO = () => {
    setCratePOModalShown(true);
  };

  // Closes the modal only on success — on failure it stays open (with the
  // user's input intact) so the submit can be retried.
  const savePO = async (data) => {
    try {
      //upload receipts
      // for (let file of data.data.receipts) {
      //   const fileName = `PO receipt_${String(Math.floor(Date.now()))}.png`;
      //   const fileData = { id: data.id, name: fileName, base64: file.base64 };
      //   console.log(fileData);
      //   await POService.attachFile(fileData);
      // }

      //save data
      let response = await POService.update(data);
      if (response.status == 201) {
        setCratePOModalShown(false);
        toast("Purchase order updated successfully!");
        setOrder(response.data);
      }
    } catch (error) {
      handleError(error);
    }

    getPurchaseOrder();
  };

  const resetReceiptDragState = () => {
    receiptDragCounter.current = 0;
    setIsReceiptDragActive(false);
    setIsReceiptDragInvalid(false);
  };

  const isAllowedReceiptFile = (file) => {
    if (!file) return false;
    const type = (file.type || "").toLowerCase();
    if (type.startsWith("image/") || type === "application/pdf") return true;
    const name = (file.name || "").toLowerCase();
    return /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|tiff?|pdf)$/.test(name);
  };

  const isAllowedReceiptDragType = (type = "") => {
    const t = type.toLowerCase();
    return t.startsWith("image/") || t === "application/pdf";
  };

  const hasInvalidDragItems = (dataTransfer) => {
    if (!dataTransfer) return false;
    const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
    const fileItems = items.filter((item) => item.kind === "file");
    if (fileItems.length === 0) return false;
    return fileItems.some((item) => !isAllowedReceiptDragType(item.type));
  };

  const filterAllowedReceiptFiles = (files = []) => {
    const allowed = [];
    let rejectedCount = 0;
    files.forEach((file) => {
      if (isAllowedReceiptFile(file)) {
        allowed.push(file);
      } else {
        rejectedCount += 1;
      }
    });
    if (rejectedCount > 0) {
      toast(
        `${rejectedCount} file${rejectedCount > 1 ? "s were" : " was"} skipped. Only images and PDFs are allowed.`
      );
    }
    return allowed;
  };

  const handleReceiptFiles = async (files = []) => {
    const options = {
      maxSizeMB: 5,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    if (files.length === 0) return;

    setIsUploadingReceipt(true);

    // Ids present before this upload, so the scan flow only runs on the
    // receipts added now.
    const previousIds = new Set((order.attachments || []).map((attachment) => attachment.id));

    try {
      let latestOrder = null;

      for (const file of files) {
        let compressedFile = file;
        const isImage = file.type.startsWith("image/");

        if (isImage && file.size > 5242880) {
          const compressedBlob = await imageCompression(file, options);
          compressedFile = new File(
            [compressedBlob],
            file.name,
            {
              type: compressedBlob.type,
              lastModified: Date.now(),
            }
          );

          console.log("Original:", file.size / 1024 / 1024, "MB");
          console.log("Compressed:", compressedFile.size / 1024 / 1024, "MB");
        }

        const response = await POService.uploadFile(compressedFile, order.id);
        if (response.status == 201) {
          latestOrder = response.data;
        } else {
          toast("Something went wrong");
          return;
        }
      }

      if (latestOrder) {
        setOrder(latestOrder);
      }

      toast(`${files.length} file${files.length > 1 ? "s" : ""} uploaded successfully`);

      // Hand the new receipts to the total-extraction flow (scan -> review).
      const newReceipts = (latestOrder?.attachments || []).filter(
        (attachment) => !previousIds.has(attachment.id),
      );
      if (newReceipts.length) setReceiptScanQueue(newReceipts);
    } catch (error) {
      handleError(error);
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const receiptSelected = async (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    await handleReceiptFiles(files);
    if (receiptInputFile.current) {
      receiptInputFile.current.value = null;
    } else if (input) {
      input.value = null;
    }
  };

  const uploadReceipt = () => {
    receiptInputFile.current.click();
  };

  const handleReceiptDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUploadingReceipt) return;

    receiptDragCounter.current += 1;
    setIsReceiptDragActive(true);
    setIsReceiptDragInvalid(hasInvalidDragItems(e.dataTransfer));
  };

  const handleReceiptDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUploadingReceipt) return;

    setIsReceiptDragActive(true);
    setIsReceiptDragInvalid(hasInvalidDragItems(e.dataTransfer));
  };

  const handleReceiptDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUploadingReceipt) return;

    receiptDragCounter.current = Math.max(receiptDragCounter.current - 1, 0);
    if (receiptDragCounter.current === 0) {
      setIsReceiptDragActive(false);
      setIsReceiptDragInvalid(false);
    }
  };

  const handleReceiptDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUploadingReceipt || !can("po:update")) return;

    const files = Array.from(e.dataTransfer.files || []);
    resetReceiptDragState();
    const allowedFiles = filterAllowedReceiptFiles(files);
    if (allowedFiles.length === 0) return;
    await handleReceiptFiles(allowedFiles);
  };

  const deleteAttachment = () => {
    setAttachmentDeleteModalShown(false);
    POService.deleteAttachment(attachmentToDeleteId)
      .then((response) => {
        toast("Attachment deleted successfully");
        getPurchaseOrder();
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const openAttachmentSlider = (attachments, activeIndex = 0) => {
    setSliderAttachments(attachments);
    setSliderActiveIndex(activeIndex);
    setIsAttachmentSliderShown(true);
  };

  const getAttachmentPreview = (attachment, options = {}) => ({
    id: options.id || attachment.id || attachment.fileName,
    fileName: attachment.fileName,
    displayName: getRealFileName(attachment.fileName) || attachment.fileName || "Attachment",
    isPdf: attachment.fileName?.toLowerCase().endsWith(".pdf"),
    isReceipt: options.isReceipt || false,
    description: attachment.description,
    tags: attachment.tags,
    createdBy: attachment.createdBy?.name || attachment.createdBy,
    createdAt: attachment.createdAt,
    ...(options.url ? { url: options.url } : {}),
  });

  const previewReceiptAttachment = (clickedAttachment) => {
    const attachments = (order.attachments || []).map((attachment) =>
      getAttachmentPreview(attachment, { isReceipt: true })
    );
    const activeIndex = attachments.findIndex((a) => a.id === clickedAttachment.id);
    openAttachmentSlider(attachments, activeIndex >= 0 ? activeIndex : 0);
  };

  const previewPOItemAttachment = (fileName) => {
    openAttachmentSlider(
      [
        getAttachmentPreview(
          { fileName },
          {
            url: getUploadUrl(fileName),
          }
        ),
      ],
      0
    );
  };

  if (!order) {
    return <Loader variant="block" className="mt-10" ariaLabel="loading-purchase-order" />;
  }

  // Both kinds of PO carry items; only a detailed one prices them. The card
  // follows the PO's own mode rather than the rows, so it agrees with the
  // document even on a detailed PO whose prices aren't filled in yet.
  const isDetailed = order.mode === "detailed";
  const shipToLines = [
    order.shipToName,
    order.shipToAddress,
    [[order.shipToCity, order.shipToState].filter(Boolean).join(", "), order.shipToZip].filter(Boolean).join(" "),
  ].filter(Boolean);

  return (
    <div className="w-full px-5 pb-5">
      <AttachmentImageSlider
        shown={isAttachmentSliderShown}
        attachments={sliderAttachments}
        activeIndex={sliderActiveIndex}
        onCancel={() => setIsAttachmentSliderShown(false)}
      />
      <PONumberAddModal
        shown={poNumberModalShown}
        onSaveClicked={(poNumber) => approveRequest(poNumber)}
        onCancelClicked={() => setPoNumberModalShown(false)}
      />
      <CreatePoModal
        shown={cratePOModalShown}
        onCancelClicked={() => setCratePOModalShown(false)}
        onSubmitClicked={savePO}
        wo={order.wo}
        editingPO={order}
      />

      <PreviewDownloadPdfModal shown={pdfModalShown} order={order} onClose={() => setPdfModalShown(false)} />

      <ConfirmDialog
        title="DELETE ATTACHMENT"
        description="Are you sure you want to delete this attachment?"
        shown={attachmentDeleteModalShown}
        onConfirmClicked={deleteAttachment}
        onCancelClicked={() => setAttachmentDeleteModalShown(false)}
      />

      {/* Total-extraction flow for receipts uploaded just now (scan -> review).
          Keyed by the first receipt id so a fresh upload restarts the flow. */}
      {receiptScanQueue?.length > 0 && (
        <PoReceiptScanFlow
          key={receiptScanQueue[0].id}
          receipts={receiptScanQueue}
          onFinished={() => {
            setReceiptScanQueue(null);
            getPurchaseOrder();
          }}
        />
      )}

      <div className="w-full h-[56px] flex items-center justify-between">
        <div onClick={() => navigate(-1)} className="w-full h-full flex items-center cursor-pointer">
          <img src={getImageUrl("/images/common/back_arrow.png")} className="w-[24px]" />
          <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
        </div>
        {/* <div
          onClick={async () => {
            try {
              const fileURL = getPOReceiptUrl(`PO_Report_${order.number}.pdf`);
              const response = await axios.head(fileURL);
              if (response.status === 200) {
                downloadPdf(getPOReceiptUrl(`PO_Report_${order.number}.pdf`), `PO_Report_${order.number}.pdf`);
              }
            } catch (error) {
            console.log("🚀 PO receipt doesn't exist:", error);

            POService.generatePOReceipt(order.id)
              .then((response) => {
                downloadPdf(getPOReceiptUrl(`PO_Report_${order.number}.pdf`), `PO_Report_${order.number}.pdf`);
              })
              .catch((error) => {
                console.log("🚀 ~ PODetail ~ error:", error);
              });

            // }
          }}
          className="w-[150px] cursor-pointer px-[20px] h-[30px] bg-blueColor rounded-[6px] flex items-center justify-center font-OpenSansRegular font-[600] text-[12px] text-white"
        >
          Download PDF
        </div> */}

        <div className="flex items-center justify-center gap-2">
          {can("po:update") && order.status !== 4 && (
            <CompleteButton
              onClicked={() => {
                setIsCompleting(true);
                POService.update({
                  id: order.id,
                  data: { status: 4 },
                  eventUser: UserService.getCurrentUser(),
                })
                  .then((response) => {
                    if (response.status === 201) {
                      getPurchaseOrder();
                    }
                  })
                  .catch((error) => {
                    handleError(error);
                  })
                  .finally(() => {
                    setIsCompleting(false);
                  });
              }}
              isLoading={isCompleting}
            />
          )}
        </div>
      </div>

      {/* Purchase Order details */}
      <div className="w-full pb-6 bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-[#E5E8F3] flex flex-col justify-start items-stretch overflow-hidden">
        <div className="self-stretch px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px]">
              Purchase Order Details
            </span>
            <POStatusComponent value={order.status} />
          </div>
          <div className="flex items-center gap-2">
            {(order.status === 0 || order.status === 1) && currentUser.roles[0].id <= 2 && (
              <>
                <ApproveRequestButton onClicked={showPoInputModal} />
                <DenyRequestButton onClicked={denyRequest} />
              </>
            )}
            <DownloadPdfbutton onClicked={() => setPdfModalShown(true)} isLoading={false} />
            {canEditPo(order) && <PencilButton clicked={editPO} label="Edit PO" />}
          </div>
        </div>

        <div className="w-full flex justify-between items-stretch gap-6 px-6 pt-5">
          <div className="flex-1 min-w-0 grid grid-cols-3 gap-x-6 gap-y-5">
            <Field label="PO #">{order.number || "—"}</Field>
            <Field label={vocabularize("WO Reference #")}>
              {order.wo?.id ? (
                <span
                  onClick={() => window.open("/work/" + String(order.wo.id), "_blank")}
                  className="text-[#0065FD] cursor-pointer hover:underline"
                >
                  {order.wo?.number}
                </span>
              ) : (
                order.wo?.number || "—"
              )}
            </Field>
            <Field label="Date Added">{order.createdAt ? formatDate(order.createdAt) : "—"}</Field>
            <Field label="Payment Type">{paymentItems[order.paymentType] || "—"}</Field>
            <Field label="Customer" capitalize>
              {order.wo?.customer?.companyName || "—"}
            </Field>
            <Field label="Vendor" capitalize>
              {order.vendor || "—"}
            </Field>
            {/* The optional blocks print on the PDF, so they are shown here only
                when the PO actually carries them — an empty Quote # or Ship To on
                every simple purchase reads as missing data rather than absent. */}
            {order.quoteNumber && <Field label="Quote #">{order.quoteNumber}</Field>}
            {shipToLines.length > 0 && (
              <Field label="Ship To" capitalize>
                {shipToLines.join(", ")}
              </Field>
            )}
            {order.shipVia && <Field label="Ship Via">{order.shipVia}</Field>}
            {order.issuedUser && order.status >= 2 && (
              <Field label="Issued For">
                <PersonValue avatar={order.issuedUser.avatar} name={order.issuedUser.name} />
              </Field>
            )}
            {order.issuedBy && (
              <Field label="Issued By">
                <PersonValue avatar={order.issuedBy.avatar} name={order.issuedBy.name} />
              </Field>
            )}

            <div className="col-span-3 flex flex-col justify-start items-start gap-1">
              <div className={LABEL_CLASS}>Description</div>
              <div className="self-stretch text-[14px] leading-5 break-words">
                {order.description ? (
                  <span className="text-[#1A2332] font-OpenSansSemiBold">{order.description}</span>
                ) : (
                  <span className="text-[#7C829A] italic font-OpenSansRegular">No description provided.</span>
                )}
              </div>
            </div>

            {/* Internal only — deliberately never rendered on the PDF. */}
            <div className="col-span-3 flex flex-col justify-start items-start gap-1">
              <div className={LABEL_CLASS}>Note</div>
              <div className="self-stretch text-[14px] leading-5 break-words">
                {order.note ? (
                  <span className="text-[#1A2332] font-OpenSansSemiBold">{order.note}</span>
                ) : (
                  <span className="text-[#7C829A] italic font-OpenSansRegular">No note added.</span>
                )}
              </div>
            </div>
          </div>

          <div className="w-[30%] flex flex-col justify-start items-start gap-3 pl-6 border-l border-[#E5E8F3]">
            <div className={LABEL_CLASS}>Attachments</div>
            <input
              onChange={receiptSelected}
              type="file"
              id="receiptfile"
              ref={receiptInputFile}
              style={{ display: "none" }}
              multiple
              accept="image/*,application/pdf"
            />
            <div
              className={`w-full rounded-lg border border-dashed p-3 transition-colors ${
                isReceiptDragActive && isReceiptDragInvalid
                  ? "border-red-500 bg-red-50"
                  : isReceiptDragActive
                    ? "border-[#0065FD] bg-[#F4F8FF]"
                    : "border-[#C9CEE2]"
              }`}
              onDragEnter={handleReceiptDragEnter}
              onDragOver={handleReceiptDragOver}
              onDragLeave={handleReceiptDragLeave}
              onDrop={handleReceiptDrop}
            >
              {order.attachments?.length > 0 ? (
                <div className="w-full max-h-[260px] overflow-y-auto grid grid-cols-2 gap-3">
                  {order.attachments.map((attachment, index) => (
                    <ReceiptThumbnail
                      key={index}
                      attachment={attachment}
                      onPreview={() => previewReceiptAttachment(attachment)}
                      onDownload={() => {
                        if (attachment.fileName.split(".").pop().toLowerCase() === "pdf") {
                          downloadPdf(getPOReceiptUrl(attachment.fileName), getRealFileName(attachment.fileName));
                        } else {
                          downloadImage(getPOReceiptUrl(attachment.fileName), getRealFileName(attachment.fileName));
                        }
                      }}
                      canDelete={can("po:deleteAttachment")}
                      onDelete={() => {
                        setAttachmentDeleteModalShown(true);
                        setAttachmentToDeleteId(attachment.id);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-[#7C829A] italic font-OpenSansRegular text-[13px] leading-5">
                  No attachments added yet.
                </p>
              )}
              {can("po:update") && (
                <>
                  <p className="mt-2 text-[#7C829A] font-OpenSansRegular text-[12px] leading-[18px]">
                    Drag and drop files here, or click upload.
                  </p>
                  <div className="mt-2">
                    <WhiteUploadButton isLoading={isUploadingReceipt} clicked={uploadReceipt} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="w-full mt-3 pb-2 bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border border-[#E5E8F3] flex flex-col justify-start items-stretch overflow-hidden">
        <div className="self-stretch px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px]">Items</span>
          <span className="text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase">
            {order.poItems.length} {order.poItems.length === 1 ? "line" : "lines"}
          </span>
        </div>

        <div className="w-full px-6 pt-4">
          {order.poItems.length > 0 ? (
            <>
              {/* The pricing columns appear only on a detailed PO. A simple one
                  lists descriptions — three empty columns would read as data
                  the page failed to load. */}
              {isDetailed && (
                <div className="w-full flex items-center pb-2 border-b border-[#E5E8F3]">
                  <span className={`flex-1 ${LABEL_CLASS}`}>Item / Description</span>
                  <span className={`w-[80px] text-right ${LABEL_CLASS}`}>Qty</span>
                  <span className={`w-[100px] text-right ${LABEL_CLASS}`}>Rate</span>
                  <span className={`w-[110px] text-right ${LABEL_CLASS}`}>Amount</span>
                </div>
              )}

              {order.poItems.map((item, index) => (
                <div
                  key={index}
                  className={`w-full flex items-start py-3 ${
                    index < order.poItems.length - 1 ? "border-b border-[#E5E8F3]" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0 flex flex-col gap-1 pr-4">
                    {item.itemNumber && (
                      <span className="text-[#1A2332] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] break-words">
                        {item.itemNumber}
                      </span>
                    )}
                    {item.description && (
                      <span
                        className={`text-[14px] leading-5 break-words ${
                          item.itemNumber
                            ? "text-[#7C829A] font-OpenSansRegular"
                            : "text-[#1A2332] font-OpenSansSemiBold"
                        }`}
                      >
                        {item.description}
                      </span>
                    )}
                    {item.attachment && (
                      <div className="flex items-center gap-3">
                        <span
                          className="text-[#1A2332] font-OpenSansRegular text-[13px] leading-5 cursor-pointer hover:underline"
                          onClick={() => previewPOItemAttachment(item.attachment)}
                        >
                          {getRealFileName(item.attachment)}
                        </span>
                        <div
                          onClick={() => downloadImage(getUploadUrl(item.attachment), getRealFileName(item.attachment))}
                          className="cursor-pointer"
                        >
                          <FaDownload color="#1A2332" size={12} />
                        </div>
                      </div>
                    )}
                  </div>
                  {isDetailed && (
                    <>
                      <span className={`w-[80px] text-right ${VALUE_CLASS}`}>{item.qty ?? ""}</span>
                      <span className={`w-[100px] text-right ${VALUE_CLASS}`}>
                        {item.rate != null ? `$${formatMoney(item.rate)}` : ""}
                      </span>
                      <span className={`w-[110px] text-right ${VALUE_CLASS}`}>
                        {item.amount != null ? `$${formatMoney(item.amount)}` : ""}
                      </span>
                    </>
                  )}
                </div>
              ))}

              {order.total != null && (
                <div className="w-full flex items-center py-3 border-t border-[#E5E8F3]">
                  <span className="flex-1 text-right pr-4 text-[#7C829A] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase">
                    Total
                  </span>
                  <span className="w-[110px] text-right text-[#1A2332] font-OpenSansSemiBold text-[16px] leading-6">
                    ${formatMoney(order.total)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <p className="pb-4 text-[#7C829A] italic font-OpenSansRegular text-[14px] leading-5">
              No items on this purchase order.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PODetail;
